import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Stack,
  TextInput,
  Select,
  Textarea,
  NumberInput,
  Button,
  Title,
  Text,
  Stepper,
  Group,
  Paper,
  Divider,
  Grid,
  Box,
  Modal,
  TagsInput,
  FileInput,
  Switch,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { DatePickerInput } from "@mantine/dates";
import { Currencies } from "../../../../lib/data/currencies";
import { Countries } from "../../../../lib/data/countries";
import { DashboardEndpoints } from "../../../../lib/endpoints-dashboard";
import {
  PerformRequest,
  usePerformRequest,
} from "../../../../lib/usePerformRequest";
import { useToastNotification } from "../../../../hooks/useToast";
import { HttpCodes } from "../../../../lib/data";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useOrganizationsContext } from "../../../../context/OrganizationsContext";
import { IEventProps } from "../../../../types";
import { Loader } from "../../../../components/ui/loader";
import {
  Calendar,
  DollarSign,
  Users,
  User,
  FileText,
  MapPin,
  Video,
  Globe,
  Plus,
  Trash2,
  Ticket,
  Edit2,
  Building2,
  UserCircle,
  ArrowRight,
} from "lucide-react";
import GoogleMapPicker from "./GoogleMapPicker";
import "./styles.scss";

// Transform currencies from data file to Mantine Select format
const currencies = Currencies.map((currency) => ({
  value: currency.code,
  label: `${currency.code} - ${currency.name}`,
}));

// Transform countries from data file to Mantine Select format
const countries = Countries.map((country) => ({
  value: country.code,
  label: country.name,
}));

const eventTypes = [
  { value: "online", label: "Online" },
  { value: "hybrid", label: "Hybrid" },
  { value: "offline", label: "Offline" },
];

const videoConferencePlatforms = [
  { value: "zoom", label: "Zoom" },
  { value: "google-meet", label: "Google Meet" },
  { value: "microsoft-teams", label: "Microsoft Teams" },
  { value: "webex", label: "Webex" },
  { value: "other", label: "Other" },
];

interface Ticket {
  ticket_type: string;
  currency: string;
  price: number | "";
  quantity: number | "";
  features_list: string[];
  color: string;
}

const eventCategories = [
  { value: "Conference", label: "Conference" },
  { value: "Workshop", label: "Workshop" },
  { value: "Seminar", label: "Seminar" },
  { value: "Meetup", label: "Meetup" },
  { value: "Concert", label: "Concert" },
  { value: "Festival", label: "Festival" },
  { value: "Exhibition", label: "Exhibition" },
  { value: "Networking", label: "Networking" },
  { value: "Training", label: "Training" },
  { value: "Other", label: "Other" },
];

interface EventFormData {
  event_type: "user" | "organization";
  organization_id: string | "";
  name: string;
  date: Date | null;
  start_time: string;
  end_time: string;
  type: "online" | "hybrid" | "offline";
  short_description: string;
  full_description: string;
  category: string;
  features_included: string[];
  cover_image: File | null;
  host_name: string;
  organizer_description: string;
  event_description: string;
  is_paid_event: boolean;
  // Location fields
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude: number;
  longitude: number;
  video_conference_link: string;
  video_conference_platform: string;
  video_conference_id: string;
  video_conference_password: string;
  tickets: Ticket[];
}

const NewEventPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToastNotification();
  const [activeStep, setActiveStep] = useState(0);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [editingTicketIndex, setEditingTicketIndex] = useState<number | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);
  const isEditMode = !!id;
  const eventId = id ? parseInt(id, 10) : null;

  // Get event type from URL params
  const eventTypeFromUrl = searchParams.get("type") as
    | "user"
    | "organization"
    | null;

  // Get organizations from context
  const { organizations, isLoading: isLoadingOrgs } = useOrganizationsContext();

  const organizationOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id?.toString() || "",
      label: org.name || "Unknown Organization",
    }));
  }, [organizations]);

  // Update form when event type changes from URL
  useEffect(() => {
    if (
      eventTypeFromUrl &&
      (eventTypeFromUrl === "user" || eventTypeFromUrl === "organization")
    ) {
      form.setFieldValue("event_type", eventTypeFromUrl);
    }
  }, [eventTypeFromUrl]);

  // Fetch event data when in edit mode
  useEffect(() => {
    const fetchEventData = async () => {
      if (!isEditMode || !eventId) return;

      setIsLoadingEvent(true);
      try {
        const endpoint = DashboardEndpoints.GetSingleEvent(eventId);
        const response = await PerformRequest<{ event: IEventProps }>({
          method: endpoint.method as any,
          route: endpoint.route,
        });

        if (response.status === 200 && response.data?.event) {
          const event = response.data.event;

          // Parse date from string (format: DD-MM-YYYY or ISO string)
          let eventDate: Date | null = null;
          if (event.date) {
            try {
              // Try parsing as ISO string first
              eventDate = new Date(event.date);
              // If invalid, try DD-MM-YYYY format
              if (isNaN(eventDate.getTime())) {
                const parts = event.date.split("-");
                if (parts.length === 3) {
                  eventDate = new Date(
                    parseInt(parts[2], 10),
                    parseInt(parts[1], 10) - 1,
                    parseInt(parts[0], 10)
                  );
                }
              }
              // If still invalid, set to null
              if (isNaN(eventDate.getTime())) {
                eventDate = null;
              }
            } catch {
              eventDate = null;
            }
          }

          // Parse time from date string if available
          // Check if start_time and end_time are in the response
          const eventAny = event as any;
          let startTime = eventAny.start_time || "";
          let endTime = eventAny.end_time || "";

          // If times are not in the response, try to parse from date
          if (!startTime && event.date) {
            try {
              const dateObj = new Date(event.date);
              if (!isNaN(dateObj.getTime())) {
                const hours = String(dateObj.getHours()).padStart(2, "0");
                const minutes = String(dateObj.getMinutes()).padStart(2, "0");
                startTime = `${hours}:${minutes}`;
                // Default end time to 1 hour after start
                const endDate = new Date(dateObj);
                endDate.setHours(endDate.getHours() + 1);
                const endHours = String(endDate.getHours()).padStart(2, "0");
                const endMinutes = String(endDate.getMinutes()).padStart(
                  2,
                  "0"
                );
                endTime = `${endHours}:${endMinutes}`;
              }
            } catch {
              // Keep empty strings if parsing fails
            }
          }

          // Determine event type (user or organization)
          const eventType: "user" | "organization" = event.organization
            ? "organization"
            : "user";

          // Get organization ID if it exists
          const organizationId = event.organization
            ? (event.organization as any).id?.toString() || ""
            : "";

          // Parse features_included - might be a string or array
          // Note: features_included might not be in the interface but could be in the API response
          let featuresIncluded: string[] = [];
          if (eventAny.features_included) {
            if (Array.isArray(eventAny.features_included)) {
              featuresIncluded = eventAny.features_included;
            } else if (typeof eventAny.features_included === "string") {
              featuresIncluded = eventAny.features_included
                .split(",")
                .map((f: string) => f.trim())
                .filter((f: string) => f.length > 0);
            }
          }

          // Parse location data
          const location = (event.location || {}) as any;
          const locationData = {
            address: location.address || "",
            city: location.city || "",
            state: location.state || "",
            country: location.country || "",
            postal_code: location.postal_code || "",
            latitude: location.latitude
              ? parseFloat(String(location.latitude))
              : 0,
            longitude: location.longitude
              ? parseFloat(String(location.longitude))
              : 0,
            video_conference_link: location.video_conference_link || "",
            video_conference_platform: location.video_conference_platform || "",
            video_conference_id: location.video_conference_id || "",
            video_conference_password: location.video_conference_password || "",
          };

          // Fetch tickets for the event
          // Tickets might be in the response or need to be fetched separately
          let tickets: Ticket[] = [];
          try {
            if (eventAny.tickets && Array.isArray(eventAny.tickets)) {
              tickets = eventAny.tickets.map((ticket: any) => ({
                ticket_type: ticket.ticket_type || "",
                currency: ticket.currency?.code || ticket.currency || "",
                price: ticket.price
                  ? typeof ticket.price === "string"
                    ? parseFloat(ticket.price)
                    : ticket.price
                  : 0,
                quantity: ticket.quantity || 0,
                features_list: ticket.features_list
                  ? Array.isArray(ticket.features_list)
                    ? ticket.features_list
                    : typeof ticket.features_list === "string"
                    ? ticket.features_list
                        .split(",")
                        .map((f: string) => f.trim())
                    : []
                  : [],
                color: ticket.color || "#8b5cf6",
              }));
            }
          } catch (error) {
            console.error("Error parsing tickets:", error);
            tickets = [];
          }

          // Populate form with event data
          form.setValues({
            event_type: eventType,
            organization_id: organizationId,
            name: event.name || "",
            date: eventDate,
            start_time: startTime,
            end_time: endTime,
            type: event.type || "online",
            short_description:
              eventAny.short_description ||
              event.event_description?.substring(0, 500) ||
              "",
            full_description:
              eventAny.full_description || event.event_description || "",
            category: eventAny.category || "",
            features_included: featuresIncluded,
            cover_image: null, // We can't set a file from URL, user can re-upload
            host_name: event.host_name || "",
            organizer_description: event.organizer_description || "",
            event_description: event.event_description || "",
            is_paid_event: event.is_paid_event || false,
            ...locationData,
            tickets: tickets,
          });
        } else {
          toast.error("Failed to load event data");
          navigate("/d/events");
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        toast.error("An error occurred while loading the event");
        navigate("/d/events");
      } finally {
        setIsLoadingEvent(false);
      }
    };

    fetchEventData();
  }, [isEditMode, eventId]);

  const handleEventTypeSelection = (type: "user" | "organization") => {
    setSearchParams({ type });
    form.setFieldValue("event_type", type);
  };

  const form = useForm<EventFormData>({
    initialValues: {
      event_type: eventTypeFromUrl || "user",
      organization_id: "",
      name: "",
      date: null,
      start_time: "",
      end_time: "",
      type: "online",
      short_description: "",
      full_description: "",
      category: "",
      features_included: [],
      cover_image: null,
      host_name: "",
      organizer_description: "",
      event_description: "",
      is_paid_event: false,
      // Location fields
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      latitude: 0,
      longitude: 0,
      video_conference_link: "",
      video_conference_platform: "",
      video_conference_id: "",
      video_conference_password: "",
      tickets: [],
    },
    validate: {
      event_type: (value) => (value ? null : "Event type is required"),
      organization_id: (value, values) => {
        if (
          values.event_type === "organization" &&
          (!value || value === "" || value === null)
        ) {
          return "Organization is required for organization events";
        }
        return null;
      },
      name: (value) =>
        value.trim().length > 0 ? null : "Event name is required",
      date: (value) => {
        if (!value) return "Date is required";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(value);
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          return "Event date cannot be in the past";
        }
        return null;
      },
      start_time: (value) => {
        if (!value || (typeof value === "string" && !value.trim())) {
          return "Start time is required";
        }
        const timeString = typeof value === "string" ? value : "";
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(timeString)) {
          return "Start time is required";
        }
        return null;
      },
      end_time: (value, values) => {
        if (!value || (typeof value === "string" && !value.trim())) {
          return "End time is required";
        }
        const timeString = typeof value === "string" ? value : "";
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(timeString)) {
          return "End time is required";
        }
        // Validate end time is after start time
        if (values.start_time && timeString) {
          const startTimeStr =
            typeof values.start_time === "string" ? values.start_time : "";
          if (startTimeStr && timeRegex.test(startTimeStr)) {
            const [startHour, startMin] = startTimeStr.split(":").map(Number);
            const [endHour, endMin] = timeString.split(":").map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;
            if (endMinutes <= startMinutes) {
              return "End time must be after start time";
            }
          }
        }
        return null;
      },
      type: (value) => (value ? null : "Event type is required"),
      short_description: (value) => {
        if (!value || !value.trim()) return "Short description is required";
        if (value.length > 500) {
          return "Short description must be 500 characters or less";
        }
        return null;
      },
      full_description: (value) => {
        if (!value || !value.trim()) return "Full description is required";
        return null;
      },
      category: (value) => (!value ? "Category is required" : null),
      host_name: (value) =>
        value.trim().length > 0 ? null : "Host name is required",
      organizer_description: (value) =>
        value.trim().length > 0 ? null : "Organizer description is required",
      event_description: (value) =>
        value.trim().length > 0 ? null : "Event description is required",
      // Location validation
      address: (value, values) => {
        if (values.type !== "online" && !value.trim()) {
          return "Address is required for offline and hybrid events";
        }
        return null;
      },
      city: (value, values) => {
        if (values.type !== "online" && !value.trim()) {
          return "City is required for offline and hybrid events";
        }
        return null;
      },
      state: (value, values) => {
        if (values.type !== "online" && !value.trim()) {
          return "State is required for offline and hybrid events";
        }
        return null;
      },
      country: (value, values) => {
        if (values.type !== "online" && !value) {
          return "Country is required for offline and hybrid events";
        }
        return null;
      },
      postal_code: (value, values) => {
        if (values.type !== "online" && !value.trim()) {
          return "Postal code is required for offline and hybrid events";
        }
        return null;
      },
      video_conference_link: (value, values) => {
        if (
          (values.type === "online" || values.type === "hybrid") &&
          !value.trim()
        ) {
          return "Video conference link is required for online and hybrid events";
        }
        return null;
      },
      video_conference_platform: (value, values) => {
        if ((values.type === "online" || values.type === "hybrid") && !value) {
          return "Video conference platform is required for online and hybrid events";
        }
        return null;
      },
      tickets: (value) => {
        if (!value || value.length === 0) {
          return "At least one ticket is required";
        }
        return null;
      },
    },
    validateInputOnBlur: true,
  });

  const handleNext = () => {
    // Validate current step before proceeding
    const stepFields = getStepFields(activeStep);

    // Special handling for tickets validation
    if (activeStep === 2) {
      if (form.values.tickets.length === 0) {
        form.setFieldError("tickets", "At least one ticket is required");
        return;
      }

      // Validate each ticket
      let hasTicketErrors = false;
      form.values.tickets.forEach((ticket, index) => {
        if (!ticket.ticket_type || !ticket.ticket_type.trim()) {
          form.setFieldError(
            `tickets.${index}.ticket_type` as any,
            "Ticket type is required"
          );
          hasTicketErrors = true;
        }
        if (!ticket.currency) {
          form.setFieldError(
            `tickets.${index}.currency` as any,
            "Currency is required"
          );
          hasTicketErrors = true;
        }
        if (
          ticket.price === "" ||
          ticket.price === null ||
          (typeof ticket.price === "number" && ticket.price < 0)
        ) {
          form.setFieldError(
            `tickets.${index}.price` as any,
            "Price cannot be negative"
          );
          hasTicketErrors = true;
        }
        if (
          ticket.quantity === "" ||
          ticket.quantity === null ||
          (typeof ticket.quantity === "number" && ticket.quantity <= 0)
        ) {
          form.setFieldError(
            `tickets.${index}.quantity` as any,
            "Quantity must be greater than 0"
          );
          hasTicketErrors = true;
        }
      });

      if (hasTicketErrors) {
        return;
      }
    }

    const validation = form.validate();
    const hasStepErrors = stepFields.some(
      (field) => validation.errors[field as keyof EventFormData]
    );

    if (!hasStepErrors) {
      setActiveStep((current) => Math.min(current + 1, 2));
    }
  };

  // Ticket modal form
  const ticketForm = useForm<Ticket>({
    initialValues: {
      ticket_type: "",
      currency: "",
      price: "",
      quantity: "",
      features_list: [],
      color: "#8b5cf6",
    },
    validate: {
      ticket_type: (value) =>
        !value || !value.trim() ? "Ticket type is required" : null,
      currency: (value) => (!value ? "Currency is required" : null),
      price: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "Price is required";
        }
        if (typeof value === "number" && value < 0) {
          return "Price cannot be negative";
        }
        return null;
      },
      quantity: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "Quantity is required";
        }
        if (typeof value === "number" && value <= 0) {
          return "Quantity must be greater than 0";
        }
        return null;
      },
      color: (value) => {
        if (!value || !value.trim()) return "Color is required";
        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (!hexRegex.test(value)) {
          return "Color must be a valid hex color (e.g., #8b5cf6)";
        }
        return null;
      },
    },
  });

  const openTicketModal = (index: number | null = null) => {
    if (index !== null) {
      // Editing existing ticket
      const ticket = form.values.tickets[index];
      ticketForm.setValues({
        ticket_type: ticket.ticket_type,
        currency: ticket.currency,
        price: ticket.price,
        quantity: ticket.quantity,
        features_list: ticket.features_list || [],
        color: ticket.color || "#8b5cf6",
      });
      setEditingTicketIndex(index);
    } else {
      // Adding new ticket
      ticketForm.reset();
      setEditingTicketIndex(null);
    }
    setTicketModalOpen(true);
  };

  const closeTicketModal = () => {
    setTicketModalOpen(false);
    setEditingTicketIndex(null);
    ticketForm.reset();
  };

  const saveTicket = () => {
    const validation = ticketForm.validate();
    if (validation.hasErrors) {
      return;
    }

    const ticketData = ticketForm.values;

    if (editingTicketIndex !== null) {
      // Update existing ticket
      form.setFieldValue(`tickets.${editingTicketIndex}`, ticketData);
    } else {
      // Add new ticket
      form.insertListItem("tickets", ticketData);
    }

    closeTicketModal();
  };

  const removeTicket = (index: number) => {
    form.removeListItem("tickets", index);
  };

  const handlePrevious = () => {
    setActiveStep((current) => Math.max(current - 1, 0));
  };

  const getStepFields = (step: number): (keyof EventFormData)[] => {
    switch (step) {
      case 0:
        return [
          "organization_id",
          "name",
          "date",
          "start_time",
          "end_time",
          "type",
          "category",
          "short_description",
          "full_description",
          "features_included",
          "cover_image",
          "host_name",
          "organizer_description",
          "event_description",
        ];
      case 1:
        return [
          "address",
          "city",
          "state",
          "country",
          "postal_code",
          "latitude",
          "longitude",
          "video_conference_link",
          "video_conference_platform",
          "video_conference_id",
          "video_conference_password",
        ];
      case 2:
        return ["tickets"] as (keyof EventFormData)[];
      default:
        return [];
    }
  };

  const handleSubmit = async (values: EventFormData) => {
    setIsSubmitting(true);

    // Format date to DD-MM-YYYY
    const formatDate = (date: Date | null | string | undefined): string => {
      if (!date) {
        toast.error("Event date is required");
        setIsSubmitting(false);
        return "";
      }

      try {
        // Convert to Date object if it's not already one
        let dateObj: Date;

        if (date instanceof Date) {
          dateObj = date;
        } else if (typeof date === "string") {
          dateObj = new Date(date);
        } else if (typeof date === "number") {
          // Might be a timestamp
          dateObj = new Date(date);
        } else {
          // Last resort - try to construct a Date
          dateObj = new Date(date as any);
        }

        // Check if date is valid
        if (!dateObj || isNaN(dateObj.getTime())) {
          console.error("Invalid date value:", date);
          toast.error("Invalid date format. Please select a valid date.");
          setIsSubmitting(false);
          return "";
        }

        // Double-check that it's actually a Date object
        if (!(dateObj instanceof Date)) {
          console.error("Date is not a Date object:", dateObj);
          toast.error("Date object is invalid. Please select the date again.");
          setIsSubmitting(false);
          return "";
        }

        const day = String(dateObj.getDate()).padStart(2, "0");
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const year = dateObj.getFullYear();
        return `${day}-${month}-${year}`;
      } catch (error) {
        console.error("Error formatting date:", error, "Date value:", date);
        toast.error(
          "Error formatting date. Please try selecting the date again."
        );
        setIsSubmitting(false);
        return "";
      }
    };

    // Transform form data to API payload structure
    const formattedDate = formatDate(values.date);

    // If date formatting failed, stop submission
    if (!formattedDate) {
      return;
    }

    // Create FormData object to send all data as form-data
    const formData = new FormData();

    // Append basic fields
    formData.append("name", values.name);
    formData.append("date", formattedDate);
    formData.append("type", values.type);
    formData.append("short_description", values.short_description);
    formData.append("full_description", values.full_description);
    formData.append("start_time", values.start_time);
    formData.append("end_time", values.end_time);
    formData.append("category", values.category);
    formData.append("host_name", values.host_name);
    formData.append("organizer_description", values.organizer_description);
    formData.append("event_description", values.event_description);
    formData.append("is_paid_event", values.is_paid_event ? "true" : "false");

    // Append features_included as comma-separated string
    formData.append("features_included", values.features_included.join(", "));

    // Append cover image file if exists
    if (values.cover_image) {
      formData.append("cover_image", values.cover_image);
    }

    // Append location object as JSON string
    const location = {
      address: values.address,
      city: values.city,
      state: values.state,
      country: values.country,
      postal_code: values.postal_code,
      latitude: values.latitude,
      longitude: values.longitude,
      video_conference_link: values.video_conference_link,
      video_conference_platform: values.video_conference_platform,
      video_conference_id: values.video_conference_id,
      video_conference_password: values.video_conference_password,
    };
    formData.append("location", JSON.stringify(location));

    // Append tickets array as JSON string
    const tickets = values.tickets.map((ticket) => ({
      ticket_type: ticket.ticket_type,
      currency: ticket.currency,
      price: typeof ticket.price === "number" ? ticket.price : 0,
      quantity: typeof ticket.quantity === "number" ? ticket.quantity : 0,
      features_list: ticket.features_list.join(", "),
      color: ticket.color || "#8b5cf6",
    }));
    formData.append("tickets", JSON.stringify(tickets));

    // Add organization_id only if event_type is organization
    if (values.event_type === "organization" && values.organization_id) {
      const orgId = parseInt(String(values.organization_id), 10);
      if (!isNaN(orgId)) {
        formData.append("organization_id", String(orgId));
      }
    }

    try {
      const endpoint =
        isEditMode && eventId
          ? DashboardEndpoints.GetUpdateEvent(eventId)
          : DashboardEndpoints.GetCreateEvent();

      const response = await PerformRequest<any>({
        method: endpoint.method as any,
        route: endpoint.route,
        data: formData,
        onError: () => {
          setIsSubmitting(false);
        },
      });

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        toast.success(
          isEditMode
            ? "Event updated successfully!"
            : "Event created successfully!"
        );
        // Navigate to events list or event detail page
        navigate("/d/events");
      } else {
        toast.error(
          response.message ||
            (isEditMode
              ? "Failed to update event. Please try again."
              : "Failed to create event. Please try again.")
        );
      }
    } catch (error) {
      toast.error(
        isEditMode
          ? "An error occurred while updating the event. Please try again."
          : "An error occurred while creating the event. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack gap="xl" className="form-content">
            {/* Organization Selection - Only show if organization event */}
            {form.values.event_type === "organization" && (
              <>
                <Box className="form-section">
                  <Text size="sm" fw={600} mb="md" className="section-title">
                    Organization Details
                  </Text>
                  <Stack gap="md">
                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Select
                          label="Organization"
                          placeholder={
                            isLoadingOrgs
                              ? "Loading organizations..."
                              : "Select organization"
                          }
                          data={organizationOptions}
                          searchable
                          required
                          size="md"
                          disabled={isLoadingOrgs}
                          classNames={{
                            label: "form-label",
                            input: "form-input",
                          }}
                          {...form.getInputProps("organization_id")}
                        />
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Box>
                <Divider className="form-divider" />
              </>
            )}

            {/* Basic Information Section */}
            <Box className="form-section">
              <Text size="sm" fw={600} mb="md" className="section-title">
                Basic Information
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Event Name"
                  placeholder="Enter event name"
                  required
                  size="md"
                  classNames={{
                    label: "form-label",
                    input: "form-input",
                  }}
                  {...form.getInputProps("name")}
                />

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <DatePickerInput
                      label="Event Date"
                      placeholder="Select event date"
                      required
                      size="md"
                      minDate={new Date()}
                      leftSection={<Calendar size={18} />}
                      classNames={{
                        label: "form-label",
                      }}
                      styles={{
                        input: {
                          border: "1.5px solid #d0d5dd",
                          borderRadius: "6px",
                          fontSize: "0.95rem",
                          transition: "all 0.2s ease",
                          backgroundColor: "#ffffff",
                          paddingLeft: "44px",
                        },
                      }}
                      {...form.getInputProps("date")}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label="Event Type"
                      placeholder="Select event type"
                      data={eventTypes}
                      required
                      size="md"
                      classNames={{
                        label: "form-label",
                        input: "form-input",
                      }}
                      {...form.getInputProps("type")}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      type="time"
                      label="Start Time"
                      placeholder="Enter start time (24h format)"
                      required
                      size="md"
                      classNames={{
                        label: "form-label",
                        input: "form-input",
                      }}
                      value={form.values.start_time || ""}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        form.setFieldValue("start_time", value);
                      }}
                      error={form.errors.start_time}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      type="time"
                      label="End Time"
                      placeholder="Enter end time (24h format)"
                      required
                      size="md"
                      classNames={{
                        label: "form-label",
                        input: "form-input",
                      }}
                      value={form.values.end_time || ""}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        form.setFieldValue("end_time", value);
                      }}
                      error={form.errors.end_time}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Category"
                      placeholder="Select category"
                      data={eventCategories}
                      required
                      size="md"
                      searchable
                      classNames={{
                        label: "form-label",
                        input: "form-input",
                      }}
                      {...form.getInputProps("category")}
                    />
                  </Grid.Col>
                </Grid>

                <FileInput
                  label="Cover Image"
                  placeholder="Upload event cover image"
                  accept="image/*"
                  size="md"
                  classNames={{
                    label: "form-label",
                  }}
                  {...form.getInputProps("cover_image")}
                />

                <Textarea
                  label="Short Description"
                  placeholder="Brief description (max 500 characters)"
                  required
                  minRows={2}
                  maxLength={500}
                  size="md"
                  classNames={{
                    label: "form-label",
                    input: "form-textarea",
                  }}
                  {...form.getInputProps("short_description")}
                />

                <Textarea
                  label="Full Description"
                  placeholder="Detailed description of the event..."
                  required
                  minRows={4}
                  size="md"
                  classNames={{
                    label: "form-label",
                    input: "form-textarea",
                  }}
                  {...form.getInputProps("full_description")}
                />

                <TagsInput
                  label="Features Included"
                  placeholder="Add features (e.g., Networking lunch, Digital materials)"
                  size="md"
                  classNames={{
                    label: "form-label",
                    input: "form-input",
                  }}
                  {...form.getInputProps("features_included")}
                />

                <Switch
                  label="Paid Event"
                  description="Enable this if your event requires payment for tickets"
                  size="md"
                  classNames={{
                    label: "form-label",
                  }}
                  {...form.getInputProps("is_paid_event", { type: "checkbox" })}
                />
              </Stack>
            </Box>

            <Divider className="form-divider" />

            {/* Host & Description Section */}
            <Box className="form-section">
              <Text size="sm" fw={600} mb="md" className="section-title">
                Host & Description
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Host Name"
                  placeholder="Enter host name"
                  required
                  size="md"
                  leftSection={<User size={18} />}
                  classNames={{
                    label: "form-label",
                  }}
                  styles={{
                    input: {
                      border: "1.5px solid #d0d5dd",
                      borderRadius: "6px",
                      fontSize: "0.95rem",
                      transition: "all 0.2s ease",
                      backgroundColor: "#ffffff",
                      paddingLeft: "44px",
                    },
                  }}
                  {...form.getInputProps("host_name")}
                />

                <Textarea
                  label="Organizer Description"
                  placeholder="Describe the organizer, their background, and expertise..."
                  required
                  minRows={3}
                  size="md"
                  classNames={{
                    label: "form-label",
                    input: "form-textarea",
                  }}
                  {...form.getInputProps("organizer_description")}
                />

                <Textarea
                  label="Event Description"
                  placeholder="Describe the event, what attendees will learn or experience, key highlights..."
                  required
                  minRows={4}
                  size="md"
                  classNames={{
                    label: "form-label",
                    input: "form-textarea",
                  }}
                  {...form.getInputProps("event_description")}
                />
              </Stack>
            </Box>
          </Stack>
        );

      case 1:
        return (
          <Stack gap="xl" className="form-content">
            {/* Physical Location Section - Only for offline and hybrid */}
            {(form.values.type === "offline" ||
              form.values.type === "hybrid") && (
              <>
                <Box className="form-section">
                  <Text size="sm" fw={600} mb="md" className="section-title">
                    Physical Location
                  </Text>
                  <Stack gap="md">
                    <TextInput
                      label="Address"
                      placeholder="Enter street address"
                      required
                      size="md"
                      leftSection={<MapPin size={18} />}
                      classNames={{
                        label: "form-label",
                      }}
                      styles={{
                        input: {
                          border: "1.5px solid #d0d5dd",
                          borderRadius: "6px",
                          fontSize: "0.95rem",
                          transition: "all 0.2s ease",
                          backgroundColor: "#ffffff",
                          paddingLeft: "44px",
                        },
                      }}
                      {...form.getInputProps("address")}
                    />

                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          label="City"
                          placeholder="Enter city"
                          required
                          size="md"
                          classNames={{
                            label: "form-label",
                            input: "form-input",
                          }}
                          {...form.getInputProps("city")}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          label="State/Province"
                          placeholder="Enter state or province"
                          required
                          size="md"
                          classNames={{
                            label: "form-label",
                            input: "form-input",
                          }}
                          {...form.getInputProps("state")}
                        />
                      </Grid.Col>
                    </Grid>

                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 8 }}>
                        <Select
                          label="Country"
                          placeholder="Select country"
                          data={countries}
                          searchable
                          required
                          size="md"
                          leftSection={<Globe size={18} />}
                          classNames={{
                            label: "form-label",
                          }}
                          styles={{
                            input: {
                              border: "1.5px solid #d0d5dd",
                              borderRadius: "6px",
                              fontSize: "0.95rem",
                              transition: "all 0.2s ease",
                              backgroundColor: "#ffffff",
                              paddingLeft: "44px",
                            },
                          }}
                          {...form.getInputProps("country")}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 4 }}>
                        <TextInput
                          label="Postal Code"
                          placeholder="Enter postal code"
                          required
                          size="md"
                          classNames={{
                            label: "form-label",
                            input: "form-input",
                          }}
                          {...form.getInputProps("postal_code")}
                        />
                      </Grid.Col>
                    </Grid>

                    <Box>
                      <Text size="sm" fw={600} mb="sm" className="form-label">
                        Select Location on Map
                      </Text>
                      <Text size="xs" c="dimmed" mb="md">
                        Click on the map or drag the marker to set the exact
                        location
                      </Text>
                      <GoogleMapPicker
                        latitude={form.values.latitude}
                        longitude={form.values.longitude}
                        onLocationChange={(lat, lng) => {
                          form.setFieldValue("latitude", lat);
                          form.setFieldValue("longitude", lng);
                        }}
                      />
                      <Grid mt="md">
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <NumberInput
                            label="Latitude"
                            placeholder="Latitude"
                            value={form.values.latitude}
                            onChange={(value) =>
                              form.setFieldValue(
                                "latitude",
                                typeof value === "number" ? value : 0
                              )
                            }
                            decimalScale={6}
                            size="md"
                            readOnly
                            classNames={{
                              label: "form-label",
                              input: "form-input",
                            }}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <NumberInput
                            label="Longitude"
                            placeholder="Longitude"
                            value={form.values.longitude}
                            onChange={(value) =>
                              form.setFieldValue(
                                "longitude",
                                typeof value === "number" ? value : 0
                              )
                            }
                            decimalScale={6}
                            size="md"
                            readOnly
                            classNames={{
                              label: "form-label",
                              input: "form-input",
                            }}
                          />
                        </Grid.Col>
                      </Grid>
                    </Box>
                  </Stack>
                </Box>

                {(form.values.type === "offline" ||
                  form.values.type === "hybrid") && (
                  <Divider className="form-divider" />
                )}
              </>
            )}

            {/* Video Conference Section - Only for online and hybrid */}
            {(form.values.type === "online" ||
              form.values.type === "hybrid") && (
              <Box className="form-section">
                <Text size="sm" fw={600} mb="md" className="section-title">
                  Video Conference Details
                </Text>
                <Stack gap="md">
                  <Select
                    label="Video Conference Platform"
                    placeholder="Select platform"
                    data={videoConferencePlatforms}
                    required
                    size="md"
                    leftSection={<Video size={18} />}
                    classNames={{
                      label: "form-label",
                    }}
                    styles={{
                      input: {
                        border: "1.5px solid #d0d5dd",
                        borderRadius: "6px",
                        fontSize: "0.95rem",
                        transition: "all 0.2s ease",
                        backgroundColor: "#ffffff",
                        paddingLeft: "44px",
                      },
                    }}
                    {...form.getInputProps("video_conference_platform")}
                  />

                  <TextInput
                    label="Video Conference Link"
                    placeholder="https://zoom.us/j/..."
                    required
                    size="md"
                    classNames={{
                      label: "form-label",
                      input: "form-input",
                    }}
                    {...form.getInputProps("video_conference_link")}
                  />

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Meeting ID"
                        placeholder="Enter meeting ID"
                        size="md"
                        classNames={{
                          label: "form-label",
                          input: "form-input",
                        }}
                        {...form.getInputProps("video_conference_id")}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Meeting Password"
                        placeholder="Enter meeting password"
                        type="password"
                        size="md"
                        classNames={{
                          label: "form-label",
                          input: "form-input",
                        }}
                        {...form.getInputProps("video_conference_password")}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Box>
            )}
          </Stack>
        );

      case 2:
        return (
          <Stack gap="xl" className="form-content">
            <Box className="form-section">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={600} className="section-title">
                  Event Tickets
                </Text>
                <Button
                  leftSection={<Plus size={16} />}
                  onClick={() => openTicketModal(null)}
                  size="sm"
                  variant="light"
                  className="nav-button"
                >
                  Add Ticket
                </Button>
              </Group>

              {form.values.tickets.length === 0 ? (
                <Paper p="xl" withBorder className="empty-tickets">
                  <Stack align="center" gap="md">
                    <Ticket size={48} color="var(--mantine-color-dimmed)" />
                    <Text c="dimmed" ta="center">
                      No tickets added yet. Click "Add Ticket" to create your
                      first ticket.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <Stack gap="md">
                  {form.values.tickets.map((ticket, index) => (
                    <Paper
                      key={index}
                      p="lg"
                      withBorder
                      className="ticket-card"
                    >
                      <Group justify="space-between" align="flex-start">
                        <Group gap="lg" style={{ flex: 1 }}>
                          <Box
                            style={{
                              padding: "12px",
                              background: ticket.color || "#8b5cf6",
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Ticket size={24} color="white" />
                          </Box>
                          <Stack gap={4} style={{ flex: 1 }}>
                            <Group gap="md" align="center">
                              <Text fw={700} size="lg" c="dark">
                                {ticket.ticket_type || "Untitled Ticket"}
                              </Text>
                              <Text
                                size="sm"
                                fw={600}
                                c="dimmed"
                                style={{
                                  padding: "4px 12px",
                                  background: "#f2f4f7",
                                  borderRadius: "6px",
                                }}
                              >
                                {ticket.currency}
                              </Text>
                            </Group>
                            <Group gap="xl" mt={4}>
                              <Group gap={4}>
                                <DollarSign size={16} color="#667085" />
                                <Text size="sm" c="dimmed">
                                  Price:
                                </Text>
                                <Text size="sm" fw={600} c="dark">
                                  {typeof ticket.price === "number"
                                    ? ticket.price === 0
                                      ? "Free"
                                      : new Intl.NumberFormat("en-US", {
                                          style: "currency",
                                          currency: ticket.currency || "USD",
                                          minimumFractionDigits: 2,
                                        }).format(ticket.price)
                                    : "Not set"}
                                </Text>
                              </Group>
                              <Group gap={4}>
                                <Users size={16} color="#667085" />
                                <Text size="sm" c="dimmed">
                                  Quantity:
                                </Text>
                                <Text size="sm" fw={600} c="dark">
                                  {ticket.quantity || "Not set"}
                                </Text>
                              </Group>
                            </Group>
                            {ticket.features_list &&
                              ticket.features_list.length > 0 && (
                                <Group gap="xs" mt="xs" wrap="wrap">
                                  {ticket.features_list.map((feature, idx) => (
                                    <Text
                                      key={idx}
                                      size="xs"
                                      style={{
                                        padding: "4px 8px",
                                        background: "#f2f4f7",
                                        borderRadius: "4px",
                                        color: "#667085",
                                      }}
                                    >
                                      {feature}
                                    </Text>
                                  ))}
                                </Group>
                              )}
                          </Stack>
                        </Group>
                        <Group gap="xs">
                          <Button
                            variant="light"
                            size="sm"
                            leftSection={<Edit2 size={14} />}
                            onClick={() => openTicketModal(index)}
                            className="nav-button"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="subtle"
                            color="red"
                            size="sm"
                            leftSection={<Trash2 size={14} />}
                            onClick={() => removeTicket(index)}
                            disabled={form.values.tickets.length === 1}
                          >
                            Remove
                          </Button>
                        </Group>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}

              {form.errors.tickets && (
                <Text size="sm" c="red" mt="xs">
                  {typeof form.errors.tickets === "string"
                    ? form.errors.tickets
                    : "Please fix ticket errors"}
                </Text>
              )}
            </Box>
          </Stack>
        );

      default:
        return null;
    }
  };

  // Show loading state while fetching event data in edit mode
  if (isEditMode && isLoadingEvent) {
    return (
      <div className="new-event-page" data-form-page>
        <Container size="xl" py="xl">
          <Stack
            align="center"
            gap="md"
            style={{ minHeight: "400px", justifyContent: "center" }}
          >
            <Loader size="lg" />
            <Text>Loading event data...</Text>
          </Stack>
        </Container>
      </div>
    );
  }

  return (
    <div className="new-event-page" data-form-page>
      <Container size="xl" py="xl">
        {!eventTypeFromUrl && !isEditMode ? (
          <Stack gap="xl" align="center">
            <Box
              className="page-header"
              style={{ textAlign: "center", maxWidth: "600px" }}
            >
              <Title order={1} className="page-title">
                {isEditMode ? "Edit Event" : "Create New Event"}
              </Title>
              <Text className="page-description">
                {isEditMode
                  ? "Update your event details"
                  : "Choose the type of event you want to create"}
              </Text>
            </Box>

            <Grid
              gutter="xl"
              style={{ width: "100%", maxWidth: "900px" }}
              mt="xl"
            >
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper
                  p="xl"
                  withBorder
                  className="event-type-card"
                  style={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    height: "100%",
                    border: "2px solid #e4e7ec",
                  }}
                  onClick={() => handleEventTypeSelection("user")}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#0f5eea";
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(15, 94, 234, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e4e7ec";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Stack gap="lg" align="center">
                    <Box
                      style={{
                        padding: "24px",
                        background:
                          "linear-gradient(135deg, #0f5eea 0%, #7f56d9 100%)",
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <UserCircle size={64} color="white" />
                    </Box>
                    <Stack gap="xs" align="center">
                      <Title order={3} style={{ textAlign: "center" }}>
                        User Event
                      </Title>
                      <Text c="dimmed" ta="center" size="sm">
                        Create an event as an individual user. Perfect for
                        personal events, hangouts, meetups, and community
                        gatherings.
                      </Text>
                    </Stack>
                    <Button
                      rightSection={<ArrowRight size={16} />}
                      size="md"
                      className="nav-button primary"
                      fullWidth
                      mt="md"
                    >
                      Select User Event
                    </Button>
                  </Stack>
                </Paper>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper
                  p="xl"
                  withBorder
                  className="event-type-card"
                  style={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    height: "100%",
                    border: "2px solid #e4e7ec",
                  }}
                  onClick={() => handleEventTypeSelection("organization")}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#0f5eea";
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(15, 94, 234, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e4e7ec";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Stack gap="lg" align="center">
                    <Box
                      style={{
                        padding: "24px",
                        background:
                          "linear-gradient(135deg, #0f5eea 0%, #7f56d9 100%)",
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Building2 size={64} color="white" />
                    </Box>
                    <Stack gap="xs" align="center">
                      <Title order={3} style={{ textAlign: "center" }}>
                        Organization Event
                      </Title>
                      <Text c="dimmed" ta="center" size="sm">
                        Create an event on behalf of an organization. Ideal for
                        corporate events, conferences, and branded experiences.
                      </Text>
                    </Stack>
                    <Button
                      rightSection={<ArrowRight size={16} />}
                      size="md"
                      className="nav-button primary"
                      fullWidth
                      mt="md"
                    >
                      Select Organization Event
                    </Button>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Stack>
        ) : (
          <Stack gap="xl">
            <Box className="page-header">
              <Group justify="space-between" align="flex-start">
                <Box>
                  <Title order={1} className="page-title">
                    {isEditMode ? "Edit Event" : "Create New Event"}
                  </Title>
                  <Text className="page-description">
                    {isEditMode
                      ? "Update the event details below. Make sure to review all information before saving."
                      : "Fill in the event details step by step. You can always come back to edit later."}
                  </Text>
                </Box>
                <Button
                  variant="subtle"
                  onClick={() => {
                    setSearchParams({});
                    form.reset();
                  }}
                  size="sm"
                >
                  Change Event Type
                </Button>
              </Group>
            </Box>

            <Box className="stepper-wrapper">
              <Stepper
                active={activeStep}
                onStepClick={setActiveStep}
                classNames={{
                  root: "custom-stepper",
                  step: "custom-stepper-step",
                  stepBody: "custom-stepper-body",
                  stepLabel: "custom-stepper-label",
                  stepDescription: "custom-stepper-description",
                }}
              >
                <Stepper.Step
                  label="Event Information"
                  description="Basic event details"
                >
                  <Paper className="form-paper" p="xl" mt="xl">
                    {renderStepContent()}
                  </Paper>
                </Stepper.Step>
                <Stepper.Step
                  label="Location Details"
                  description="Event location information"
                >
                  <Paper className="form-paper" p="xl" mt="xl">
                    {renderStepContent()}
                  </Paper>
                </Stepper.Step>
                <Stepper.Step
                  label="Tickets"
                  description="Ticketing information"
                >
                  <Paper className="form-paper" p="xl" mt="xl">
                    {renderStepContent()}
                  </Paper>
                </Stepper.Step>
                <Stepper.Completed>
                  <Paper className="form-paper" p="xl" mt="xl">
                    <Text>Completed! Review your event details.</Text>
                  </Paper>
                </Stepper.Completed>
              </Stepper>
            </Box>

            <Group justify="space-between" mt="xl" className="form-actions">
              <Button
                variant="default"
                size="md"
                onClick={handlePrevious}
                disabled={activeStep === 0}
                className="nav-button"
              >
                Previous
              </Button>

              {activeStep < 2 ? (
                <Button
                  size="md"
                  onClick={handleNext}
                  className="nav-button primary"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  size="md"
                  onClick={async () => {
                    // Validate tickets first
                    if (form.values.tickets.length === 0) {
                      form.setFieldError(
                        "tickets",
                        "At least one ticket is required"
                      );
                      return;
                    }

                    let hasTicketErrors = false;
                    form.values.tickets.forEach((ticket, index) => {
                      if (!ticket.ticket_type || !ticket.ticket_type.trim()) {
                        form.setFieldError(
                          `tickets.${index}.ticket_type` as any,
                          "Ticket type is required"
                        );
                        hasTicketErrors = true;
                      }
                      if (!ticket.currency) {
                        form.setFieldError(
                          `tickets.${index}.currency` as any,
                          "Currency is required"
                        );
                        hasTicketErrors = true;
                      }
                      if (
                        ticket.price === "" ||
                        ticket.price === null ||
                        (typeof ticket.price === "number" && ticket.price < 0)
                      ) {
                        form.setFieldError(
                          `tickets.${index}.price` as any,
                          "Price cannot be negative"
                        );
                        hasTicketErrors = true;
                      }
                      if (
                        ticket.quantity === "" ||
                        ticket.quantity === null ||
                        (typeof ticket.quantity === "number" &&
                          ticket.quantity <= 0)
                      ) {
                        form.setFieldError(
                          `tickets.${index}.quantity` as any,
                          "Quantity must be greater than 0"
                        );
                        hasTicketErrors = true;
                      }
                    });

                    if (hasTicketErrors) {
                      return;
                    }

                    const validation = form.validate();
                    if (!validation.hasErrors) {
                      await handleSubmit(form.values);
                    }
                  }}
                  className="nav-button primary"
                  loading={isSubmitting}
                >
                  {isEditMode ? "Update Event" : "Create Event"}
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Container>

      {/* Ticket Modal */}
      <Modal
        opened={ticketModalOpen}
        onClose={closeTicketModal}
        title={
          <Group gap="xs">
            <Box
              style={{
                padding: "8px",
                background: "linear-gradient(135deg, #0f5eea 0%, #7f56d9 100%)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Ticket size={20} color="white" />
            </Box>
            <Text fw={700} size="lg">
              {editingTicketIndex !== null ? "Edit Ticket" : "Add New Ticket"}
            </Text>
          </Group>
        }
        centered
        size="lg"
        className="ticket-modal"
        styles={{
          header: {
            padding: "24px 28px",
            borderBottom: "1px solid #e4e7ec",
            background: "#ffffff",
          },
          body: {
            padding: "28px",
          },
          content: {
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Stack gap="xl">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Ticket Type"
                placeholder="e.g., Basic, Premium, VIP"
                required
                size="md"
                leftSection={<Ticket size={18} />}
                classNames={{
                  label: "form-label",
                }}
                styles={{
                  input: {
                    border: "1.5px solid #d0d5dd",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    transition: "all 0.2s ease",
                    backgroundColor: "#ffffff",
                    paddingLeft: "44px",
                  },
                }}
                {...ticketForm.getInputProps("ticket_type")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Currency"
                placeholder="Select currency"
                data={currencies}
                searchable
                required
                size="md"
                leftSection={<DollarSign size={18} />}
                classNames={{
                  label: "form-label",
                }}
                styles={{
                  input: {
                    border: "1.5px solid #d0d5dd",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    transition: "all 0.2s ease",
                    backgroundColor: "#ffffff",
                    paddingLeft: "44px",
                  },
                }}
                {...ticketForm.getInputProps("currency")}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap={4}>
                <NumberInput
                  label="Price"
                  placeholder="Enter ticket price"
                  required
                  min={0}
                  decimalScale={2}
                  size="md"
                  thousandSeparator=","
                  classNames={{
                    label: "form-label",
                    input: "form-input",
                  }}
                  {...ticketForm.getInputProps("price")}
                />
                {ticketForm.values.price === 0 && (
                  <Text size="xs" c="blue">
                    Price is set to 0 — attendees will see this ticket as free.
                  </Text>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput
                label="Quantity Available"
                placeholder="Enter quantity"
                required
                min={1}
                size="md"
                leftSection={<Users size={18} />}
                classNames={{
                  label: "form-label",
                }}
                styles={{
                  input: {
                    border: "1.5px solid #d0d5dd",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    transition: "all 0.2s ease",
                    backgroundColor: "#ffffff",
                    paddingLeft: "44px",
                  },
                }}
                {...ticketForm.getInputProps("quantity")}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack gap="xs">
                <Text size="sm" fw={500} className="form-label">
                  Ticket Color
                </Text>
                <Group gap="xs">
                  <input
                    type="color"
                    value={ticketForm.values.color}
                    onChange={(e) =>
                      ticketForm.setFieldValue("color", e.target.value)
                    }
                    style={{
                      width: "60px",
                      height: "38px",
                      border: "1.5px solid #d0d5dd",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  />
                  <TextInput
                    placeholder="#8b5cf6"
                    value={ticketForm.values.color}
                    onChange={(e) =>
                      ticketForm.setFieldValue("color", e.target.value)
                    }
                    size="md"
                    style={{ flex: 1 }}
                    classNames={{
                      input: "form-input",
                    }}
                    error={ticketForm.errors.color}
                  />
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>

          <TagsInput
            label="Features List"
            placeholder="Add features (e.g., Networking lunch, Digital materials)"
            size="md"
            classNames={{
              label: "form-label",
              input: "form-input",
            }}
            {...ticketForm.getInputProps("features_list")}
          />

          <Group
            justify="flex-end"
            gap="md"
            mt="xl"
            pt="md"
            style={{ borderTop: "1px solid #e4e7ec" }}
          >
            <Button
              variant="subtle"
              onClick={closeTicketModal}
              size="md"
              className="nav-button"
            >
              Cancel
            </Button>
            <Button
              onClick={saveTicket}
              size="md"
              className="nav-button primary"
            >
              {editingTicketIndex !== null ? "Update Ticket" : "Add Ticket"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default NewEventPage;
